import { FC, ReactNode, useCallback, useLayoutEffect, useRef, useState } from "react"

import { ReactComponent as IconChevronDown } from "@common_assets/svg/chevron-down.svg"
import { Box, Fab } from "@mui/material"
import IconWrapper from "components/common/icon/iconWrapper"
import { useTranslation } from "react-i18next"

type Side = "left" | "right"

interface IProps {
  children: ReactNode
}

// Horizontal scroller with a hidden scrollbar: a fade + arrow appears on each edge that
// still has actions behind it, so the overflow is visible without hunting for a scrollbar.
const ScrollableActions: FC<IProps> = ({ children }) => {
  const { t } = useTranslation()
  const scrollRef = useRef<HTMLDivElement>(null)
  const [edges, setEdges] = useState({ left: false, right: false })

  const updateEdges = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    setEdges({
      left: el.scrollLeft > 1,
      right: el.scrollLeft + el.clientWidth < el.scrollWidth - 1,
    })
  }, [])

  useLayoutEffect(() => {
    updateEdges()
    window.addEventListener("resize", updateEdges)
    const resizeObserver = new ResizeObserver(updateEdges)
    if (scrollRef.current) resizeObserver.observe(scrollRef.current)
    return () => {
      window.removeEventListener("resize", updateEdges)
      resizeObserver.disconnect()
    }
  }, [updateEdges])

  const scrollTowards = (side: Side) => {
    const el = scrollRef.current
    if (!el) return
    el.scrollBy({
      left: (side === "left" ? -1 : 1) * el.clientWidth * 0.7,
      behavior: "smooth",
    })
  }

  const renderEdge = (side: Side) => (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        bottom: 0,
        [side]: 0,
        width: "72px",
        display: "flex",
        alignItems: "center",
        justifyContent: side === "left" ? "flex-start" : "flex-end",
        pointerEvents: "none",
        background: (theme) =>
          `linear-gradient(to ${side === "left" ? "right" : "left"}, ${
            theme.palette.mockup.primary95
          } 40%, transparent)`,
      }}
    >
      <Fab
        size="small"
        aria-label={t(
          side === "left"
            ? "students.scrollActionsLeft"
            : "students.scrollActionsRight"
        )}
        onClick={() => scrollTowards(side)}
        sx={{
          pointerEvents: "auto",
          width: "36px",
          height: "36px",
          minHeight: "36px",
          bgcolor: "mockup.neutral100",
        }}
      >
        <IconWrapper
          color="inherit"
          width="20px"
          height="20px"
          sx={{
            display: "flex",
            transform: `rotate(${side === "left" ? 90 : -90}deg)`,
            "& svg": { width: "100%", height: "100%" },
          }}
        >
          <IconChevronDown />
        </IconWrapper>
      </Fab>
    </Box>
  )

  return (
    <Box sx={{ position: "relative", flex: 1, minWidth: 0 }}>
      <Box
        ref={scrollRef}
        data-testid="scrollable-actions"
        onScroll={updateEdges}
        sx={{
          overflowX: "auto",
          overflowY: "hidden",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {children}
      </Box>
      {edges.left && renderEdge("left")}
      {edges.right && renderEdge("right")}
    </Box>
  )
}

export default ScrollableActions
